import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * OfflineIndicator component
 * 
 * Displays a banner when the user is offline and provides information
 * about offline capabilities.
 */
const OfflineIndicator = () => {
  const { t } = useTranslation(['common']);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showDetails, setShowDetails] = useState(false);

  // Update online status when it changes
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // If online, don't render anything
  if (!isOffline) {
    return null;
  }

  return (
    <div className="offline-indicator">
      <div className="alert alert-warning mb-0" role="alert">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <i className="fas fa-wifi me-2"></i>
            <strong>{t('common:offline.status')}</strong> - {t('common:offline.message')}
          </div>
          <button 
            className="btn btn-sm btn-outline-dark" 
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? t('common:offline.hideDetails') : t('common:offline.showDetails')}
          </button>
        </div>
        
        {showDetails && (
          <div className="mt-3">
            <p>{t('common:offline.details')}</p>
            <ul>
              <li>{t('common:offline.capability1')}</li>
              <li>{t('common:offline.capability2')}</li>
              <li>{t('common:offline.capability3')}</li>
            </ul>
            <p className="mb-0">
              <small>{t('common:offline.reconnect')}</small>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflineIndicator;
